"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/feedback";
import {
  applyDeleteToNotificationsCache,
  applyDeleteToUnreadCache,
  getMarkedReadQueryKey,
  normalizeNotificationId,
} from "@/features/notifications/utils/notification-read-state";
import { useAccessToken } from "@/hooks/shared/useAccessToken";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import {
  type GetNotificationResponse,
  type GetUnreadNotificationCountResponse,
  NotificationsApi,
  type PaginatedResultOfGetNotificationResponse,
} from "@/openapi/generated";

interface UseNotificationsDeleteProps {
  userId?: number;
  onSuccess?: () => void;
}

type NotificationsQuerySnapshot = Array<
  [
    queryKey: readonly unknown[],
    data: PaginatedResultOfGetNotificationResponse | undefined,
  ]
>;

export const useNotificationsDelete = ({
  userId,
  onSuccess,
}: UseNotificationsDeleteProps = {}) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (notificationIds: string[]) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(NotificationsApi, accessToken);
      return service.apiNotificationsDelete({
        requestBody: notificationIds,
      });
    },
    onMutate: async (notificationIds) => {
      await queryClient.cancelQueries({ queryKey: [QueryKeys.Notifications] });
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.NotificationsUnread],
      });
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.NotificationsUnreadCount],
      });

      const previousNotifications =
        queryClient.getQueriesData<PaginatedResultOfGetNotificationResponse>({
          queryKey: [QueryKeys.Notifications],
        }) as NotificationsQuerySnapshot;

      const previousUnread =
        queryClient.getQueriesData<PaginatedResultOfGetNotificationResponse>({
          queryKey: [QueryKeys.NotificationsUnread],
        }) as NotificationsQuerySnapshot;

      const previousUnreadCount =
        queryClient.getQueriesData<GetUnreadNotificationCountResponse>({
          queryKey: [QueryKeys.NotificationsUnreadCount],
        });

      queryClient.setQueriesData(
        { queryKey: [QueryKeys.Notifications] },
        (
          old:
            | PaginatedResultOfGetNotificationResponse
            | GetNotificationResponse[]
            | undefined,
        ) => applyDeleteToNotificationsCache(old, notificationIds),
      );

      queryClient.setQueriesData<PaginatedResultOfGetNotificationResponse>(
        { queryKey: [QueryKeys.NotificationsUnread] },
        (old) => applyDeleteToUnreadCache(old, notificationIds),
      );

      queryClient.setQueriesData<GetUnreadNotificationCountResponse>(
        { queryKey: [QueryKeys.NotificationsUnreadCount] },
        (old) =>
          old == null
            ? old
            : {
                count: Math.max(0, Number(old.count) - notificationIds.length),
              },
      );

      if (userId) {
        const idsToRemove = new Set(
          notificationIds.map(normalizeNotificationId),
        );
        queryClient.setQueryData<string[]>(
          getMarkedReadQueryKey(userId),
          (old = []) =>
            old.filter((id) => !idsToRemove.has(normalizeNotificationId(id))),
        );
      }

      return { previousNotifications, previousUnread, previousUnreadCount };
    },
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.NotificationsUnread, userId],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.NotificationsUnreadCount, userId],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.Notifications, userId],
        });
      }

      onSuccess?.();
    },
    onError: (error: unknown, _notificationIds, context) => {
      context?.previousNotifications?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      context?.previousUnread?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      context?.previousUnreadCount?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });

      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to delete notifications";

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
      });
    },
  });
};
